import React, { useMemo, useState } from 'react';
import { Flex, Heading, Button, HStack, Box, Text, ButtonGroup, Spacer } from '@chakra-ui/react';
import { FiPlus, FiChevronLeft, FiChevronRight, FiGlobe, FiSmartphone, FiActivity } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useListLinksQuery } from '../store/linksApi';
import { LinkFilters, type FilterValue } from '../components/links/LinkFilters';
import { LinksTable } from '../components/links/LinksTable';
import { CreateLinkModal } from '../components/links/CreateLinkModal';
import { AppProfileModal } from '../components/links/AppProfileModal';
import { ExpireLinkDialog } from '../components/links/ExpireLinkDialog';
import { QrCodeModal } from '../components/links/QrCodeModal';
import { LoadingState, ErrorState, EmptyState } from '../components/common/States';
import type { Link } from '../types/link';
import { formatNumber } from '../lib/format';

const PAGE_SIZE = 10;

export const LinksListPage: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<FilterValue>({ search: '', status: 'all', channel: 'all' });
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [appProfileOpen, setAppProfileOpen] = useState(false);
  const [expiring, setExpiring] = useState<Link | null>(null);
  const [qrLink, setQrLink] = useState<Link | null>(null);

  const params = useMemo(
    () => ({
      search: filters.search || undefined,
      status: filters.status,
      channel: filters.channel,
      page,
      pageSize: PAGE_SIZE,
      sortBy: 'createdAt' as const,
      sortDir: 'desc' as const,
    }),
    [filters, page],
  );

  const { data, isLoading, isFetching, isError, error, refetch } = useListLinksQuery(params);

  const onFilterChange = (next: FilterValue) => {
    setFilters(next);
    setPage(1);
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const noLinksAtAll =
    !isLoading &&
    data?.total === 0 &&
    !filters.search &&
    filters.status === 'all' &&
    filters.channel === 'all';

  return (
    <Box>
      <Flex align="center" mb={6} gap={4} wrap="wrap">
        <Box>
          <Heading size="lg">Smart Links</Heading>
          <Text color="gray.500" fontSize="sm">
            {data
              ? `${formatNumber(data.total)} link${data.total === 1 ? '' : 's'}`
              : 'Manage tenant short links'}
          </Text>
        </Box>
        <Spacer />
        <Button leftIcon={<FiActivity />} variant="outline" onClick={() => navigate('activity')}>
          Activity
        </Button>
        <Button leftIcon={<FiGlobe />} variant="outline" onClick={() => navigate('settings/domain')}>
          Custom domain
        </Button>
        <Button leftIcon={<FiSmartphone />} variant="outline"  onClick={() => setAppProfileOpen(true)}>
          App Profile
        </Button>
        <Button bg={"var(--brand-primary)"} leftIcon={<FiPlus />} onClick={() => setCreateOpen(true)}>
          Create link
        </Button>
      </Flex>

      <Box mb={4}>
        <LinkFilters value={filters} onChange={onFilterChange} />
      </Box>

      {isLoading ? (
        <LoadingState label="Loading links…" />
      ) : isError ? (
        <ErrorState
          message={(error as { message?: string })?.message ?? 'Failed to load links'}
          onRetry={refetch}
        />
      ) : noLinksAtAll ? (
        <EmptyState
          title="No links yet"
          hint="Create your first short link to start tracking clicks and app installs."
          action={
            <Button leftIcon={<FiPlus />} onClick={() => setCreateOpen(true)}>
              Create link
            </Button>
          }
        />
      ) : (
        <>
          <Box opacity={isFetching ? 0.6 : 1} transition="opacity 0.15s">
            <LinksTable links={data?.items ?? []} onExpire={setExpiring} onShowQr={setQrLink} />
          </Box>
          <HStack mt={4} justify="space-between">
            <Text fontSize="sm" color="gray.500">
              Page {page} of {totalPages}
            </Text>
            <ButtonGroup size="sm" variant="outline" isAttached>
              <Button leftIcon={<FiChevronLeft />} isDisabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Prev
              </Button>
              <Button
                rightIcon={<FiChevronRight />}
                isDisabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </ButtonGroup>
          </HStack>
        </>
      )}

      <CreateLinkModal isOpen={createOpen} onClose={() => setCreateOpen(false)} />
      <AppProfileModal isOpen={appProfileOpen} onClose={() => setAppProfileOpen(false)} />
      <ExpireLinkDialog link={expiring} onClose={() => setExpiring(null)} />
      <QrCodeModal
        isOpen={!!qrLink}
        onClose={() => setQrLink(null)}
        url={qrLink?.shortUrl ?? ''}
        filenameBase={qrLink ? `qr-${qrLink.shortCode}` : 'qr'}
      />
    </Box>
  );
};
